import { useGlobalAction } from "@gadgetinc/react";
import { ArrowPathIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Link } from "../../../../tailwind/link";
import { api } from "../../../../../api";
import { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { handleMessageMultipleChanges } from "../../../../handleMessageMultipleChanges";
import { Button } from "../../../../tailwind/button";
import { Divider } from "../../../../tailwind/divider";
import {
  ErrorMessage,
  Field,
  Fieldset,
  Label,
  Legend,
} from "../../../../tailwind/fieldset";
import { Input } from "../../../../tailwind/input";
import SelectInput from "../../../../tailwind/select-input";
import { Textarea } from "../../../../tailwind/textarea";
import { Tooltip } from "../../../../Tooltip";
import { Select } from "../../../../tailwind/select";
import { CustomerContext } from "../../../../../providers";

export const BodyFormSection = ({
  cards,
  body,
  setBody,
  bodyRef,
  bodyError,
  paramOptions,
  nodes,
  setNodes,
  selectedNode,
  errors,
}) => {
  const [parameterToInsert, setParameterToInsert] = useState("");
  const { customer, refreshCustomer } = useContext(CustomerContext);
  let noCoupon = true;

  const paramOptionsSet = useMemo(() => {
    const result = new Set();
    if (paramOptions?.length) {
      paramOptions.forEach((option) => {
        result.add(option.value);
      });
    }
    if (customer?.customFields?.coupon_code) {
      result.add("coupon_code");
    }
    return result;
  }, [paramOptions, customer?.customFields]);

  const handleBodyChange = useCallback(
    (e) => {
      const updatedValue = e.target.value;
      setBody(updatedValue);

      let _bodyParameters = null;
      if (Object.keys(messageBodyParameterReferences)?.length) {
        _bodyParameters = Object.keys(messageBodyParameterReferences).map(
          (key) => ({
            selected: key,
            default: messageBodyParameterReferences[key] ?? "",
          }),
        );
      }
      handleMessageMultipleChanges(
        { body: updatedValue, bodyParameters: _bodyParameters },
        nodes,
        setNodes,
        selectedNode,
      );
    },
    [nodes, selectedNode, setBody, setNodes],
  );

  const updateBodyParameterReferences = (_defaultValue, _key) => {
    const _bodyParameters = Object.keys(messageBodyParameterReferences).map(
      (key) => ({
        selected: key,
        default:
          key === _key
            ? _defaultValue
            : (messageBodyParameterReferences[key] ?? ""),
      }),
    );
    handleMessageMultipleChanges(
      { bodyParameters: _bodyParameters },
      nodes,
      setNodes,
      selectedNode,
    );
  };

  const messageBodyParameterReferences = useMemo(() => {
    const _bodyParameterReferences = {};
    const { bodyParameters, body } = selectedNode?.data ?? {};

    const parametersFound = [
      ...(body ?? "").matchAll(/{{\s*([\w.]+)\s*}}/g),
    ].map((matches) => matches[1]);
    for (const variable of parametersFound) {
      _bodyParameterReferences[variable] = "";
    }

    for (const parameter of bodyParameters ?? []) {
      if (Object.hasOwn(_bodyParameterReferences, parameter.selected)) {
        _bodyParameterReferences[parameter.selected] = parameter.default ?? "";
      } else {
        delete _bodyParameterReferences[parameter.selected];
      }
    }

    return _bodyParameterReferences;
  }, [selectedNode?.data?.bodyParameters, selectedNode?.data?.body]);

  useEffect(() => {
    const _bodyParameters = Object.keys(messageBodyParameterReferences).map(
      (key) => ({
        selected: key,
        default: messageBodyParameterReferences[key] ?? "",
      }),
    );

    const currentBodyParameters = selectedNode?.data?.bodyParameters || [];
    const hasChanged =
      JSON.stringify(_bodyParameters) !== JSON.stringify(currentBodyParameters);

    if (hasChanged) {
      handleMessageMultipleChanges(
        { bodyParameters: _bodyParameters },
        nodes,
        setNodes,
        selectedNode,
      );
    }
  }, [messageBodyParameterReferences, nodes, selectedNode, setNodes]);

  const addBodyParameter = () => {
    const textarea = bodyRef.current;
    const textBefore = textarea.value.substring(0, textarea.selectionStart);
    const textAfter = textarea.value.substring(textarea.selectionEnd);
    let newCursorPosition;
    let updatedValue;

    const insertionText = `{{${parameterToInsert}}}`;
    updatedValue = `${textBefore}${insertionText}${textAfter}`;
    textarea.value = updatedValue;
    const { bodyParameters } = selectedNode?.data ?? {};
    const _bodyParameters = bodyParameters ?? [];
    const hasAlreadyBodyParameter = _bodyParameters.find(
      ({ selected }) => selected === parameterToInsert,
    );
    handleMessageMultipleChanges(
      {
        body: textarea.value,
        bodyParameters: hasAlreadyBodyParameter
          ? _bodyParameters
          : _bodyParameters.concat({
              selected: parameterToInsert,
              default: "",
            }),
      },
      nodes,
      setNodes,
    );
    setBody(textarea.value);
    newCursorPosition = textBefore.length + insertionText.length;

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categoryValidationError, setCategoryValidationError] = useState("");
  const [isFetchingCategories, setIsFetchingCategories] = useState(true);

  const [
    { fetching: activateCustomFieldCouponCodeFetching },
    activateCustomFieldCouponCode,
  ] = useGlobalAction(api.settings.activateCustomFieldCouponCode);


  const handleActivateCustomFieldsCouponCode = useCallback(async () => {
    try {
      await activateCustomFieldCouponCode({
        customerId: customer.id,
      });
      await refreshCustomer();
    } catch (error) {
      console.error("Error activating custom fields coupon code:", error);
    }
  }, [customer.id, refreshCustomer]);

  const handleCategoryChange = async (e) => {
    const _category = e.target.value;
    console.log("handleCategoryChange called with :", _category);

    setCategoryFilter(_category);
    setCategoryValidationError("");

    if (!_category) {
      return;
    }

    try {
      const result = await api.messageSkeleton.update(selectedNode.data.messageSkeletonId, {
        couponCodeCategory: _category,
      });
      console.log("Category saved succesfully", result);
    } catch (error) {
      console.error("Error saving category:", error);
    }
  };

  useEffect(() => {
    const hasCouponCodeParam = Object.keys(messageBodyParameterReferences).includes("coupon_code");
    
    if (hasCouponCodeParam && categories.length > 0 && !categoryFilter) {
      setCategoryValidationError("Please select a category when using coupon_code parameter");
    } else {
      setCategoryValidationError("");
    }
  }, [messageBodyParameterReferences, categories, categoryFilter]);

  const fetchCategories = async () => {
    setIsFetchingCategories(true);
    try {
      const response = await api.getCustomCouponCodeCategories();
      const categoriesList = response.customCouponCodes
        ?.map((item) => ({
          key: item.category,
          label: item.remaining,
        }))
        .filter(Boolean);
      setCategories(categoriesList);
      console.log("Categories loaded:", categoriesList);
    } catch (error) {
      setCategories([]);
      console.error("Error loading categories:", error);
    } finally {
      setIsFetchingCategories(false);
    }
  };

  const fetchMessageCategory = async () => {
    try {
      const result = await api.messageSkeleton.findById(selectedNode.data.messageSkeletonId, {
        select: { couponCodeCategory: true },
      });
      console.log("Campaign category:", result.couponCodeCategory);
      setCategoryFilter(result.couponCodeCategory);
    } catch (error) {
      console.error("Error fetching campaign category:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchMessageCategory();
  }, [selectedNode]);

  return (
    <Fieldset>
      <Field>
        {!cards && <Divider className="mt-5" />}

        <div className="flex justify-between items-end">
          <Legend>Body</Legend>
          <Tooltip
            size="4"
            content={
              <div>
                Text formatting:
                <br />
                *Asterisks make the text bold*
                <br />
                _Underscores make the text italic_
              </div>
            }
          />
        </div>
        <Textarea
          name="body"
          ref={bodyRef}
          value={body}
          onChange={handleBodyChange}
          invalid={bodyError}
          maxLength={1024}
          showCharacterCount
          data-testid="message-body-textarea"
        />
        {errors.bodyRequired && <ErrorMessage>Body is required</ErrorMessage>}

        <div className="flex mt-2 w-full justify-between">
          <div className="flex-grow mr-1">
            <SelectInput
              placeholder="Select a parameter"
              value={parameterToInsert}
              onChange={(value) => {
                setParameterToInsert(value);
              }}
              options={paramOptions}
              selected={parameterToInsert}
            />
          </div>

          <Button
            color="secondaryButton"
            disabled={parameterToInsert === ""}
            onClick={() => {
              addBodyParameter();
            }}
          >
            Add parameter
          </Button>
        </div>
      </Field>

      {Object.keys(messageBodyParameterReferences).map((key) => (
        <Field key={`body-parameter-reference-${key}`}>
          <Divider className="mt-5 mb-3" />
          <Label key={`body-parameter-reference-${key}-label`}>
            Parameter{" "}
            <span
              className={clsx(
                !paramOptionsSet.has(key) && !key.startsWith("global.")
                  ? "text-red-600"
                  : "",
                "font-bold",
              )}
            >
              {key}{" "}
              {!paramOptionsSet.has(key) &&
                !key.startsWith("global.") &&
                " (Unknown)"}
            </span>
          </Label>
          <div className="flex justify-between items-center">
            <Label key={`body-parameter-reference-${key}-default-value`}>
              <div className="flex items-start gap-2" style={{flexDirection: "column", paddingTop: "5px", gap: "5px"}}>
                {key === "coupon_code" ? (
                  isFetchingCategories ? (
                    null
                  ) : (
                    (() => {
                      let i = 0;
                      while (i < categories.length && noCoupon) {
                        if (Number(categories[i].label) !== 0) {
                          noCoupon = false;
                        }
                        i++;
                      }
                      return null;
                    })(),

                    categories.length === 0 || noCoupon ? (
                      <>
                        <p className="mt-2">
                          {categories.length === 0 ? "No category found. Please add one to select." : "No coupon code available in this category."}
                        </p>

                        <div className="flex flex-col w-max" style={{gap: "5px"}}>
                          <Button
                            onClick={() => fetchCategories()}
                            color="primaryButton"
                            className="w-full"
                          >
                            <ArrowPathIcon className="w-4 h-4" />
                            Refresh
                          </Button>
                          <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            href="/settings/data/coupon-codes"
                            className="underline text-xs flex items-center w-full"
                          >
                            Add a category
                            <ArrowTopRightOnSquareIcon className="w-5 h-5 ml-1" />
                          </Link>
                        </div>
                      </>
                    ) : (
                      !customer?.customFields?.coupon_code ? (
                        <>
                          <Button
                            onClick={handleActivateCustomFieldsCouponCode}
                            disabled={activateCustomFieldCouponCodeFetching}
                            color="primaryButton"
                          >
                            <ArrowPathIcon className="w-4 h-4" />
                            Activate
                          </Button>
                        </>
                      ) : (
                        <Field>
                          <Select
                            value={categoryFilter}
                            onChange={handleCategoryChange}
                            className="min-w-[200px]"
                          >
                            <option value="">Select a category</option>
                            {categories?.map((item, index) => (
                              <option key={"coupon-code-category-" + index} value={item.key}>
                                {`${item.key} (${item.label})`}
                              </option>
                            ))}
                          </Select>
                          {categoryValidationError && (
                            <ErrorMessage className="mt-1">{categoryValidationError}</ErrorMessage>
                          )}
                        </Field>
                      )
                    )
                  )
                ) : (
                  "Default value"
                )}
              </div>
            
            </Label>
            {key !== "coupon_code" && (
            <Input
              className="max-w-[75%]"
              type="text"
              key={`body-parameter-reference-${key}-default-value-input`}
              placeholder="Default value if parameter is empty"
              autoComplete="off"
              value={messageBodyParameterReferences[key] ?? ""}
              onChange={(e) =>
                updateBodyParameterReferences(e.target.value, key)
              }
            />
            )}
          </div>
        </Field>
      ))}
    </Fieldset>
  );
};
